/**
 * Derived time math for the Zeiterfassung UI. Nothing here is persisted — net
 * hours and the ISO week are computed on read (the entity stores only von/bis/
 * pauseMinutes and, for querying, the split year/week).
 */

import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Minutes since midnight for a "HH:mm" string (null when blank/invalid). */
export function timeToMinutes(value: string): number | null {
    if (!TIME_PATTERN.test(value)) {
        return null;
    }
    const [hours, minutes] = value.split(':').map(Number);
    return hours * 60 + minutes;
}

/**
 * The span "ganzer Tag" books a Gerät for. 23:59, not 24:00: a stored time of
 * day ends at 23:59, and the minute short is the price of not widening every
 * time rule for one checkbox. The flag is not stored — a row IS whole-day when
 * it carries exactly this span.
 */
export const ALL_DAY_START = '00:00';
export const ALL_DAY_END = '23:59';

export function isAllDaySpan(startTime: string, endTime: string): boolean {
    return startTime === ALL_DAY_START && endTime === ALL_DAY_END;
}

/** Net worked minutes (von→bis minus the break), or null if incomputable. */
export function netMinutes(
    von: string,
    bis: string,
    pauseMinutes: number
): number | null {
    const start = timeToMinutes(von);
    const end = timeToMinutes(bis);
    if (start === null || end === null || end <= start) {
        return null;
    }
    return Math.max(0, end - start - (pauseMinutes || 0));
}

export interface NetMinutesInput {
    startTime: string;
    endTime: string;
    pauseMinutes: number;
    /** How many of the named thing — see TimeEntryVariant.showQuantity. */
    quantity?: number;
}

/**
 * What an ENTRY comes to: its net span times its Anzahl. Two Bagger booked
 * 07:00–15:00 are sixteen Gerätestunden on one row, so everything that sums or
 * displays an entry's hours goes through here rather than `netMinutes`, which
 * is the span of a single unit and stays the answer for the VALIDITY checks.
 * A worker row's quantity is always 1, so the two agree there.
 */
export function entryNetMinutes(entry: NetMinutesInput): number | null {
    const net = netMinutes(entry.startTime, entry.endTime, entry.pauseMinutes);
    if (net === null) {
        return null;
    }
    // Guards a 0 or a missing quantity — a row not yet migrated — from zeroing
    // out an entry that plainly has hours.
    const quantity = Math.trunc(Number(entry.quantity));
    return net * (Number.isFinite(quantity) && quantity > 0 ? quantity : 1);
}

/** Minutes -> "H:MM" (empty string when null/negative). */
export function formatHoursMinutes(minutes: number | null): string {
    if (minutes === null || minutes < 0) {
        return '';
    }
    return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
}

/**
 * The same duration ZERO-PADDED — "08:45", "00:45". Used wherever times are read
 * DOWN a column (the Zeiterfassung grid, its printed sheet): the unpadded form
 * leaves a ragged edge where the eye is comparing numbers rather than reading.
 */
export function formatClock(minutes: number | null): string {
    if (minutes === null || minutes < 0) {
        return '';
    }
    const hours = String(Math.floor(minutes / 60)).padStart(2, '0');
    return `${hours}:${String(minutes % 60).padStart(2, '0')}`;
}

/**
 * ISO-8601 week + week-year for a 'YYYY-MM-DD' date (both 0 when invalid). The
 * week-year can differ from the calendar year around New Year, which is why the
 * ISO week-year is stored alongside the week. timeMath.test.ts pins the
 * year-boundary cases — these values are persisted.
 */
export function isoWeekYear(dateStr: string): { year: number; week: number } {
    if (!DATE_PATTERN.test(dateStr)) {
        return { year: 0, week: 0 };
    }
    const date = dayjs(dateStr);
    // dayjs is lenient (it would roll '2026-13-01' over); the round-trip rejects
    // any regex-shaped but non-real date.
    if (!date.isValid() || date.format('YYYY-MM-DD') !== dateStr) {
        return { year: 0, week: 0 };
    }
    return { year: date.isoWeekYear(), week: date.isoWeek() };
}

/** Today as a 'YYYY-MM-DD' string (local date). */
export function todayIso(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/** Minutes since midnight for the current local time. */
export function nowMinutes(): number {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
}

/**
 * How far ahead of the clock a time of day may still be booked: a shift is
 * written down while it is being worked — at 17:00 one already knows it runs
 * until 18:00. Anything beyond the buffer is a typo, not a plan.
 */
export const FUTURE_BUFFER_MINUTES = 60;

/**
 * The latest time of day bookable for today, as minutes since midnight
 * (now + the buffer). Can exceed 24h late in the evening — `latestBookableTime`
 * is the form that says "no cap left today".
 */
export function latestBookableMinutes(): number {
    return nowMinutes() + FUTURE_BUFFER_MINUTES;
}

/**
 * The same bound as an 'HH:mm' string for the pickers' `maxTime`, or null once
 * the buffer reaches past midnight — a bound that rolled over would read as
 * 00:xx and grey out the whole day.
 */
export function latestBookableTime(): string | null {
    const minutes = latestBookableMinutes();
    if (minutes >= 24 * 60) {
        return null;
    }
    return formatClock(minutes);
}

/**
 * True when a booked span lies beyond what may be booked. A whole-day span,
 * where the variant offers one, is exempt on today's date: "the Gerät is here
 * all day" is known in the morning. A future DATE is refused all the same.
 */
export function isFutureBooking(
    dateStr: string,
    startTime: string,
    endTime: string,
    allowAllDay: boolean
): boolean {
    if (allowAllDay && isAllDaySpan(startTime, endTime)) {
        return DATE_PATTERN.test(dateStr) && dateStr > todayIso();
    }
    return (
        isFutureDateTime(dateStr, startTime) ||
        isFutureDateTime(dateStr, endTime)
    );
}

/**
 * True when a workday date + time of day lies beyond what may be booked — a
 * date after today, or today more than FUTURE_BUFFER_MINUTES ahead of the
 * clock. Blank/invalid inputs count as not-future.
 */
export function isFutureDateTime(dateStr: string, timeStr: string): boolean {
    if (!DATE_PATTERN.test(dateStr)) {
        return false;
    }
    const today = todayIso();
    if (dateStr > today) {
        return true;
    }
    if (dateStr < today) {
        return false;
    }
    const minutes = timeToMinutes(timeStr);
    return minutes !== null && minutes > latestBookableMinutes();
}

/** Shift a 'YYYY-MM-DD' date by whole days (returns '' on invalid input). */
export function shiftDay(dateStr: string, deltaDays: number): string {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return '';
    }
    const date = new Date(`${dateStr}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    date.setUTCDate(date.getUTCDate() + deltaDays);
    return date.toISOString().slice(0, 10);
}
